import React from 'react';
import styled from 'astroturf/react';
import { Body } from 'js/design_system/Body';
import { Button } from 'js/design_system/Button';
import { Icon } from 'js/design_system/Icon';
import { ImpressionWatcher } from 'js/utils/impressions/ImpressionWatcher';
import { ImpressionEntity } from 'js/utils/impressions/watchImpressions';
import {
  NewCitationPingCopy,
  pingBodyParts,
  pingButtonLabel,
  pingHeader,
} from './newCitationPingCopy';
import { MentionPingExtra } from './mentionPingTracking';

// Phones get the bottom-sheet treatment from the design: full width, flush to the bottom
// edge, only the top corners rounded, and a drag handle above the content.
const Card = styled.div`
  @import 'stylesheets/design_system/breakpoint';

  position: relative;
  width: 328px;
  max-width: 100%;
  padding: var(--spacing-sm-24, 24px) var(--spacing-xs-16, 16px);
  background: var(--background-beige-med, #f2f0ec);
  border: 1px solid var(--stroke-dark, #141413);
  border-radius: var(--border-radius-radius-lg, 16px);

  // The shared #onsite-ping container is position:fixed with no width below 768px, so it
  // shrink-wraps whatever it holds and a percentage width resolves against that rather than
  // the viewport. Pinning the sheet itself makes the full-bleed width deterministic, and lets
  // it clear the fixed 48px mobile tab bar (BottomMenu in FocusedNavView) rather than sitting
  // underneath it. Users with no tab bar (restricted accounts) get a 48px gap instead.
  @include breakpoint(small) {
    position: fixed;
    right: 0;
    bottom: 48px;
    left: 0;
    width: auto;
    padding: var(--spacing-sm-24, 24px);
    // Only the top edge is on screen once the sheet is flush to the viewport, so the side and
    // bottom borders go. The comp specs --stroke-medium here, but that hairline reads as
    // almost nothing against the beige fill; --stroke-dark matches the desktop card.
    border: 0;
    border-top: 1px solid var(--stroke-dark, #141413);
    border-radius: 24px 24px 0 0;
  }
`;

// ImpressionWatcher's own docs warn that an absolutely positioned element inside it can stop
// the IntersectionObserver firing, so it wraps the in-flow content stack rather than the Card,
// which is fixed-position on phones.
const Content = styled(ImpressionWatcher)`
  @import 'stylesheets/design_system/breakpoint';

  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs-12, 12px);
  width: 100%;

  @include breakpoint(small) {
    gap: var(--spacing-xs-16, 16px);
  }
`;

const DragHandle = styled.div`
  @import 'stylesheets/design_system/breakpoint';

  display: none;

  @include breakpoint(small) {
    display: block;
    align-self: center;
    width: 36px;
    height: 4px;
    background: var(--stroke-light, #dddde2);
    border-radius: 2px;
  }
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: var(--spacing-xs-8, 8px);
  width: 100%;
  // Leaves room for the close button so a long header never runs under it.
  padding-right: var(--spacing-sm-24, 24px);
`;

const FeatureIcon = styled.div`
  position: relative;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  // Nudged down to sit optically centred against the first line of the header.
  margin-top: 3px;
  padding: var(--spacing-xs-4, 4px);
  color: var(--text-primary, #141413);
  background: var(--background-white, white);
  border: 1px solid var(--stroke-dark, #141413);
  border-radius: var(--border-radius-radius-sm, 4px);
`;

const NotificationDot = styled.div`
  position: absolute;
  top: -2px;
  right: -2px;
  width: 8px;
  height: 8px;
  background: var(--error-dark, #b60000);
  border-radius: 50%;
`;

const Title = styled(Body).attrs({ as: 'h2', size: 'xl', bold: true })`
  margin: 0;
  color: var(--text-primary, #141413);
`;

const Description = styled(Body).attrs({ as: 'p' })`
  width: 100%;
  margin: 0;
  color: var(--text-secondary, #535366);
`;

// The values pulled from the mention are picked out of the sentence: bold, and the darkest
// text token against the secondary grey of the surrounding copy.
//
// The weight is set here rather than left to the bold prop: .ds2-5-body-md-bold resolves
// font-weight from --type-body-md-medium-font-weight, which the token files never define and
// which carries no fallback, so the prop alone renders at regular weight.
const DynamicValue = styled(Body).attrs({ as: 'span', bold: true })`
  color: var(--text-primary, #141413);
  font-weight: var(--type-body-md-bold-font-weight, 600);
`;

const CloseButton = styled.button`
  position: absolute;
  top: var(--spacing-xs-12, 12px);
  right: var(--spacing-xs-12, 12px);
  padding: 0;
  line-height: 0;
  color: var(--text-primary, #141413);
  background: none;
  border: 0;
  cursor: pointer;
`;

export interface NewCitationPingProps extends NewCitationPingCopy {
  // Receives the click event because the legacy dismissal handler calls preventDefault on it.
  onClose: (event: React.MouseEvent) => void;
  onCtaClick: () => void;
  // Supplied by RedesignedMentionsPing, which owns the arm-dependent source names so both
  // templates report identically. The watcher lives in here rather than around this component
  // because the card is fixed-position on phones, which stops the observer firing.
  impressionSource: string;
  impressionExtra: MentionPingExtra;
  impressionEntityId: number | null;
}

/**
 * Redesigned ping shared by all five mentions pings (Figma 1299-52998 desktop, 1299-52783
 * mobile), shown to users in the `variant` arm of growth_2026_q3_new_citation_pings.
 *
 * Named for the experiment rather than the surface: despite "Citation" it covers plain
 * mentions too, and switches its wording accordingly. Distinct from the premium
 * js/liswp/CitationPing, which is a different feature.
 *
 * Purely presentational: it derives every string from the supplied mention data via
 * newCitationPingCopy and reports dismissal and click-through through callbacks, so the
 * beacons, click tracking and upgrade redirect all stay in RedesignedMentionsPing and stay
 * identical across the two arms.
 */
export const NewCitationPing: React.FC<NewCitationPingProps> = ({
  isCitation,
  citingAuthorName,
  mentionedName,
  topic,
  onClose,
  onCtaClick,
  impressionSource,
  impressionExtra,
  impressionEntityId,
}) => {
  const copy: NewCitationPingCopy = {
    isCitation,
    citingAuthorName,
    mentionedName,
    topic,
  };

  return (
    <Card role="status" aria-live="polite">
      <CloseButton
        type="button"
        aria-label="Dismiss"
        className="js-new-citation-ping-close"
        onClick={onClose}
      >
        <Icon name="close" size={20} />
      </CloseButton>

      <Content
        source={impressionSource}
        entity_id={impressionEntityId ?? undefined}
        entity_type={
          impressionEntityId != null ? ImpressionEntity.Mention : undefined
        }
        extra={impressionExtra}
      >
        <DragHandle aria-hidden="true" />

        <HeaderRow>
          <FeatureIcon>
            <Icon name="format_quote" size={20} />
            <NotificationDot />
          </FeatureIcon>
          <Title>{pingHeader(copy)}</Title>
        </HeaderRow>

        <Description>
          {pingBodyParts(copy).map((segment, index) =>
            segment.dynamic ? (
              // Segments come from a fixed-shape sentence builder, so the index is stable.
              // eslint-disable-next-line react/no-array-index-key
              <DynamicValue key={index}>{segment.text}</DynamicValue>
            ) : (
              <React.Fragment key={index}>{segment.text}</React.Fragment>
            ),
          )}
        </Description>

        <Button
          fullWidth
          iconRight="arrow_forward"
          className="js-new-citation-ping-cta"
          onClick={onCtaClick}
        >
          {pingButtonLabel(copy)}
        </Button>
      </Content>
    </Card>
  );
};

export default NewCitationPing;
